import type { ReactNode } from 'react'
import type { NextPageWithLayout, RangeKey } from '@/types'

import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Activity, Download, Gauge, Users, Wallet } from 'lucide-react'
import { downloadCsv } from '@/lib/csv'
import { TODAY, formatDate, formatWeekdayDate } from '@/lib/date'
import { Seo } from '@/components/generals/seo'
import { Button } from '@/components/ui/button'
import { PageHeader } from '@/components/dashboard/page-header'
import { SegmentedControl } from '@/components/dashboard/segmented-control'
import { KpiCard } from '@/components/dashboard/kpi-card'
import { getDelta } from '@/components/dashboard/delta-badge'
import { MrrTrendCard } from '@/components/overview/mrr-trend-card'
import { PlanDistributionCard } from '@/components/overview/plan-distribution-card'
import { MrrMovementCard } from '@/components/overview/mrr-movement-card'
import { RecentTransactionsCard } from '@/components/overview/recent-transactions-card'
import { ActivityCard } from '@/components/overview/activity-card'
import { GoalsCard } from '@/components/overview/goals-card'
import { RegionsCard } from '@/components/overview/regions-card'
import { getOverview } from '@/data/metrics'
import { CURRENT_USER } from '@/data/team'
import { RANGE_OPTIONS } from '@/constants/dashboard'

import {
    formatNumber,
    formatPercent,
    formatRupiahCompact,
    formatSignedNumber,
    formatSignedPercent
} from '@/lib/format'

const Home: NextPageWithLayout = (): ReactNode => {
    const [range, setRange] = useState<RangeKey>('30d')
    const data = useMemo(() => getOverview(range), [range])
    const { mrr, customers, churn, arpu } = data.kpis
    const firstName = CURRENT_USER.name.split(' ')[0]

    const exportReport = (): void => {
        downloadCsv(
            `arunika-ringkasan-${range}-${TODAY}.csv`,
            ['Tanggal', 'MRR (Rp)', 'MRR periode sebelumnya (Rp)'],
            [
                ['Ringkasan', `${data.label} terakhir`, `${formatDate(data.startDate)} sampai ${formatDate(data.endDate)}`],
                ['MRR', mrr.value, mrr.previous],
                ['Pelanggan berbayar', customers.value, customers.previous],
                ['Churn bulanan (%)', churn.value.toFixed(2), churn.previous.toFixed(2)],
                ['ARPU (Rp)', Math.round(arpu.value), Math.round(arpu.previous)],
                [],
                ...data.trend.map((point) => [point.tooltipLabel, point.value, point.previous])
            ]
        )

        toast.success('Laporan diunduh', {
            description: `Ringkasan ${data.label} terakhir tersimpan sebagai file CSV.`
        })
    }

    return (
        <>
            <Seo />

            <div className='space-y-6'>
                <PageHeader
                    eyebrow={formatWeekdayDate(TODAY)}
                    title={`Selamat datang kembali, ${firstName}`}
                    description='Inilah denyut bisnis langganan Arunika: pendapatan, pelanggan, dan apa saja yang berubah hari ini.'
                    actions={(
                        <>
                            <SegmentedControl
                                label='Rentang waktu'
                                value={range}
                                onChange={setRange}
                                options={RANGE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
                                className='max-w-full overflow-x-auto'
                            />

                            <Button variant='outline' size='lg' onClick={exportReport}>
                                <Download data-icon='inline-start' />
                                Unduh laporan
                            </Button>
                        </>
                    )}
                />

                <section aria-label='Indikator utama' className='grid gap-4 sm:grid-cols-2 xl:grid-cols-4'>
                    <KpiCard
                        label='MRR'
                        icon={Wallet}
                        hint='Monthly recurring revenue: pendapatan berulang bulanan dari semua langganan berbayar, termasuk akun berisiko.'
                        value={formatRupiahCompact(mrr.value)}
                        delta={{ value: formatSignedPercent(mrr.change), ...getDelta(mrr.change) }}
                        comparison={data.comparison}
                        spark={mrr.spark}
                    />

                    <KpiCard
                        label='Pelanggan berbayar'
                        icon={Users}
                        hint='Jumlah akun dengan langganan berbayar yang masih berjalan. Akun trial tidak dihitung.'
                        value={formatNumber(customers.value)}
                        delta={{ value: formatSignedNumber(customers.change), ...getDelta(customers.change, false, 0.5) }}
                        comparison={data.comparison}
                        spark={customers.spark}
                    />

                    <KpiCard
                        label='Churn bulanan'
                        icon={Activity}
                        hint='Persentase pelanggan berbayar yang berhenti, disetarakan ke periode 30 hari. Semakin rendah semakin baik.'
                        value={formatPercent(churn.value, 2)}
                        delta={{ value: `${formatSignedNumber(churn.change, 2)} poin`, ...getDelta(churn.change, true, 0.005) }}
                        comparison={data.comparison}
                        spark={churn.spark}
                    />

                    <KpiCard
                        label='ARPU'
                        icon={Gauge}
                        hint='Average revenue per user: rata-rata MRR per pelanggan berbayar.'
                        value={formatRupiahCompact(arpu.value)}
                        delta={{ value: formatSignedPercent(arpu.change), ...getDelta(arpu.change) }}
                        comparison={data.comparison}
                        spark={arpu.spark}
                    />
                </section>

                <div className='grid gap-4 xl:grid-cols-3'>
                    <MrrTrendCard data={data} className='xl:col-span-2' />
                    <PlanDistributionCard />
                </div>

                <div className='grid gap-4 xl:grid-cols-3'>
                    <MrrMovementCard data={data} />
                    <RecentTransactionsCard className='xl:col-span-2' />
                </div>

                <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-3'>
                    <ActivityCard />
                    <GoalsCard />
                    <RegionsCard className='md:col-span-2 xl:col-span-1' />
                </div>
            </div>
        </>
    )
}

export default Home
